/* ==========================================================
   WM Photo Scroll — wm-photo-scroll.js
   Version: 3.2.0
   Scroll-driven horizontal gallery. The track's X position is
   derived from the section's progress through the viewport (same
   math as the original horizontal-scroll.html port), with an
   optional pointer-drag offset composed on top of it.
   ========================================================== */

(function () {
  'use strict';

  var SELECTOR = '[data-section-type="wm-photo-scroll"]';
  var CLICK_SLOP = 6; // px of travel before a drag swallows the click that follows it

  // Handles are stashed per root so destroy() can detach the window listeners.
  var handles = new WeakMap();

  function init(root) {
    if (root.dataset.wmInit === 'true') return; // idempotent — never init the same root twice
    root.dataset.wmInit = 'true';

    const track = root.querySelector('[data-wm-track]');
    if (!track) return;

    const dragEnabled =
      root.dataset.wmDrag === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dragSpeed = parseFloat(root.dataset.wmDragSpeed) || 1;

    let maxShift = 0;
    let dragOffset = 0; // px of user drag, composed on top of the scroll-derived position
    let dragging = false;
    let lastX = 0;
    let travelled = 0;
    let swallowClick = false;
    let ticking = false;

    function calc() {
      // `track.scrollWidth` cannot be used here: once flex content overflows, browsers
      // omit the container's END padding from scrollWidth, so the track's trailing
      // `padding: 0 6vw` was missing from the travel distance and the last photo ended
      // flush against the right edge. Measure the extent explicitly instead.
      const cs = getComputedStyle(track);
      const padLeft = parseFloat(cs.paddingLeft) || 0;
      const padRight = parseFloat(cs.paddingRight) || 0;
      const kids = track.children;

      let inner = 0;
      if (kids.length) {
        // Rect deltas are transform-invariant: the live translateX shifts both edges
        // equally, so their difference is the untransformed content width.
        const first = kids[0].getBoundingClientRect();
        const last = kids[kids.length - 1].getBoundingClientRect();
        inner = last.right - first.left;
      }

      // root is the `overflow: hidden` clipper, so its clientWidth is the visible
      // span — and unlike window.innerWidth it excludes the vertical scrollbar.
      maxShift = Math.max(padLeft + inner + padRight - root.clientWidth, 0);
    }

    // Unchanged from the original port: progress goes 0 -> 1 as the section
    // travels from just-entering-bottom to just-leaving-top of the viewport.
    function scrollShift() {
      const rect = root.getBoundingClientRect();
      const total = window.innerHeight + rect.height;
      let progress = (window.innerHeight - rect.top) / total;
      progress = Math.min(Math.max(progress, 0), 1);
      return progress * maxShift;
    }

    function render() {
      const base = scrollShift();
      // Clamp the offset itself — not the result — on every frame. Clamping only
      // the result would let a drag past either end bank an offset that the user
      // then has to "pay back" before the track moves again.
      dragOffset = Math.min(Math.max(dragOffset, base - maxShift), base);
      track.style.transform = 'translateX(' + -(base - dragOffset) + 'px)';
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        render();
      });
    }

    function onResize() {
      calc();
      render();
    }

    function onLoad() {
      calc();
      render();
    }

    /* ---------- pointer drag ---------- */

    function onPointerDown(event) {
      // Touch is left alone: the track is transform-positioned rather than a
      // scroll container, so claiming touch pointers here would fight the
      // page's own vertical scrolling. Mouse and pen only.
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;

      dragging = true;
      lastX = event.clientX;
      travelled = 0;
      swallowClick = false;
      root.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
      // Stops the browser starting a native image/link drag mid-gesture.
      event.preventDefault();
    }

    function onPointerMove(event) {
      if (!dragging) return;
      const dx = event.clientX - lastX;
      lastX = event.clientX;
      travelled += Math.abs(dx);
      dragOffset += dx * dragSpeed;
      render();
    }

    function onPointerUp(event) {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      swallowClick = travelled > CLICK_SLOP;
    }

    // Capture phase: a linked photo must not navigate at the end of a drag.
    function onClick(event) {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    function onDragStart(event) {
      event.preventDefault();
    }

    /* ---------- wiring ---------- */

    // NOTE: Horizon scrolls inside `.page-wrapper` (overflow-y:auto) on desktop, not on
    // window — so a plain window 'scroll' listener never fires. `capture: true` catches the
    // scroll event from whichever element actually scrolls (page-wrapper or window).
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onLoad);

    if (dragEnabled) {
      track.addEventListener('pointerdown', onPointerDown);
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', onPointerUp);
      track.addEventListener('pointercancel', onPointerUp);
      track.addEventListener('click', onClick, true);
      track.addEventListener('dragstart', onDragStart);
    }

    handles.set(root, { onScroll: onScroll, onResize: onResize, onLoad: onLoad });

    calc();
    render();
  }

  function destroy(root) {
    if (root.dataset.wmInit !== 'true') return;
    delete root.dataset.wmInit;

    const stored = handles.get(root);
    if (!stored) return;
    window.removeEventListener('scroll', stored.onScroll, { capture: true });
    window.removeEventListener('resize', stored.onResize);
    window.removeEventListener('load', stored.onLoad);
    handles.delete(root);
    // Track listeners need no removal — the track goes away with the section.
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor — init/destroy a section when it is (re)loaded or removed.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll(SELECTOR).forEach(destroy);
  });
})();
